import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'react-bootstrap';
import {
  AuditSection,
  CampaignType,
  SECTION_LABELS,
  SkillInput,
  SkillType
} from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Design } from '@Components/designConstants';
import { SECTION_ORDER } from '../google-ads-audit-constants';
import { createAdminSkill, getAdminSkill, updateAdminSkill } from './google-ads-audit-admin-api';
import { TestPanel } from './TestPanel';

const CAMPAIGN_TYPES: CampaignType[] = ['search', 'pmax', 'shopping'];
const SKILL_TYPES: SkillType[] = ['agent', 'rule'];
const isSkillType = (value: string): value is SkillType => SKILL_TYPES.includes(value as SkillType);
const isAuditSection = (value: string): value is AuditSection =>
  SECTION_ORDER.includes(value as AuditSection);

const isDataQueries = (value: unknown): value is SkillInput['dataQueries'] => Array.isArray(value);
const isRuleConfig = (value: unknown): value is SkillInput['ruleConfig'] =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as { ratings?: unknown }).ratings);

const EMPTY_FORM: SkillInput = {
  key: '',
  label: '',
  skillType: 'agent',
  sectionId: 'account_structure',
  enabled: true,
  advertiserType: 'universal'
};

interface AdminSkillEditorProps {
  skillId?: string;
  onSaved: () => void;
  onCancel: () => void;
}

export const AdminSkillEditor = ({ skillId, onSaved, onCancel }: AdminSkillEditorProps) => {
  const mode = skillId ? 'edit' : 'create';
  const [form, setForm] = useState<SkillInput>(EMPTY_FORM);
  const [dataQueriesText, setDataQueriesText] = useState('[]');
  const [ruleConfigText, setRuleConfigText] = useState('{"ratings": []}');
  const [parseError, setParseError] = useState<string | null>(null);

  const { data: existing } = useQuery({
    queryKey: ['google-ads-audit-admin-skill', skillId],
    queryFn: () => getAdminSkill(skillId as string),
    enabled: Boolean(skillId)
  });

  useEffect(() => {
    if (!existing) return;
    setForm(existing);
    setDataQueriesText(JSON.stringify(existing.dataQueries ?? [], null, 2));
    setRuleConfigText(JSON.stringify(existing.ruleConfig ?? { ratings: [] }, null, 2));
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: (input: SkillInput) =>
      mode === 'edit' ? updateAdminSkill(skillId as string, input) : createAdminSkill(input),
    onSuccess: onSaved
  });

  const save = () => {
    let dataQueries: unknown;
    let ruleConfig: unknown;
    try {
      dataQueries = JSON.parse(dataQueriesText);
      ruleConfig = form.skillType === 'rule' ? JSON.parse(ruleConfigText) : undefined;
    } catch {
      setParseError('Data queries / rule config must be valid JSON.');
      return;
    }
    if (!isDataQueries(dataQueries)) {
      setParseError('Data queries must be a JSON array.');
      return;
    }
    if (ruleConfig !== undefined && !isRuleConfig(ruleConfig)) {
      setParseError('Rule config must be a JSON object with a "ratings" array.');
      return;
    }
    setParseError(null);
    saveMutation.mutate({
      ...form,
      dataQueries,
      ruleConfig
    });
  };

  const toggleCampaignType = (type: CampaignType) => {
    setForm(prev => {
      const current = prev.applicability?.campaignTypes ?? [];
      const campaignTypes = current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type];
      return { ...prev, applicability: { campaignTypes } };
    });
  };

  return (
    <div className='google-ads-audit-admin-skill-editor'>
      <div className='google-ads-audit-admin-skill-editor-form'>
        <div className='google-ads-audit-form-row'>
          <Form.Group>
            <Form.Label>Key</Form.Label>
            <Form.Control
              value={form.key}
              disabled={mode === 'edit'}
              onChange={e => setForm(prev => ({ ...prev, key: e.target.value }))}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Label</Form.Label>
            <Form.Control
              value={form.label}
              onChange={e => setForm(prev => ({ ...prev, label: e.target.value }))}
            />
          </Form.Group>
        </div>
        <div className='google-ads-audit-form-row'>
          <Form.Group>
            <Form.Label>Section</Form.Label>
            <Form.Select
              value={form.sectionId}
              onChange={e => {
                const { value } = e.target;
                if (isAuditSection(value)) {
                  setForm(prev => ({ ...prev, sectionId: value }));
                }
              }}
            >
              {SECTION_ORDER.map(sectionId => (
                <option key={sectionId} value={sectionId}>
                  {SECTION_LABELS[sectionId]}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={form.skillType}
              onChange={e => {
                const { value } = e.target;
                if (isSkillType(value)) {
                  setForm(prev => ({ ...prev, skillType: value }));
                }
              }}
            >
              <option value='agent'>Agent</option>
              <option value='rule'>Rule</option>
            </Form.Select>
          </Form.Group>
        </div>
        <Form.Group>
          <Form.Label>Rationale (&quot;why it matters&quot;)</Form.Label>
          <Form.Control
            as='textarea'
            rows={2}
            value={form.rationale ?? ''}
            onChange={e => setForm(prev => ({ ...prev, rationale: e.target.value }))}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Applies to campaign types</Form.Label>
          <div className='google-ads-audit-admin-checkbox-row'>
            {CAMPAIGN_TYPES.map(type => (
              <Checkbox
                key={type}
                inline
                label={type}
                checked={(form.applicability?.campaignTypes ?? []).includes(type)}
                onChange={() => toggleCampaignType(type)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group>
          <Form.Label>Data queries (JSON)</Form.Label>
          <Form.Control
            as='textarea'
            rows={4}
            value={dataQueriesText}
            onChange={e => setDataQueriesText(e.target.value)}
          />
        </Form.Group>
        {form.skillType === 'agent' ? (
          <Form.Group>
            <Form.Label>Agent instructions</Form.Label>
            <Form.Control
              as='textarea'
              rows={6}
              value={form.agentConfig?.instructions ?? ''}
              onChange={e =>
                setForm(prev => ({
                  ...prev,
                  agentConfig: { ...prev.agentConfig, instructions: e.target.value }
                }))
              }
            />
          </Form.Group>
        ) : (
          <Form.Group>
            <Form.Label>Rule config (JSON)</Form.Label>
            <Form.Control
              as='textarea'
              rows={6}
              value={ruleConfigText}
              onChange={e => setRuleConfigText(e.target.value)}
            />
          </Form.Group>
        )}
        <Checkbox
          label='Enabled'
          checked={form.enabled ?? true}
          onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
        />
        {parseError && <p className='google-ads-audit-admin-editor-error'>{parseError}</p>}
        <div className='google-ads-audit-form-row'>
          <Button onClick={save} loading={saveMutation.isPending}>
            Save
          </Button>
          <Button design={Design.NEUTRAL} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
      <TestPanel />
    </div>
  );
};
